import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ContentCopy as ContentCopyIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import StorageIcon from '@mui/icons-material/Storage';
import DataTable from '../../../sharedModules/dataTable';
import axios from '../../../sharedModules/utils/axios.utils';

const useStyles = makeStyles(() => ({
  queryPreview: {
    fontFamily: 'monospace',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '200px',
  },
  fullQuery: {
    fontFamily: 'monospace',
    fontSize: '13px',
    whiteSpace: 'pre-wrap',
    backgroundColor: '#f5f5f5',
    padding: '16px',
    borderRadius: '4px',
    overflowX: 'auto',
  },
}));

interface ServmanSqlLog {
  id: number;
  query: string;
  category: string;
  status: string;
  created_at: string;
  updated_at: string;
  loggable_type: string;
  loggable_id: number;
  db_location: string;
}

interface ApiResponse {
  servman_sql_logs: ServmanSqlLog[];
}

const ServmanSqlLogsPage: React.FC = () => {
  const classes = useStyles();
  const [sqlLogs, setSqlLogs] = useState<ServmanSqlLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedQuery, setSelectedQuery] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [statusOptions, setStatusOptions] = useState<string[]>([]);

  const fetchSqlLogs = async (status = statusFilter) => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {};
      if (status !== 'all') {
        params['ransack[status_eq]'] = status;
      }
      const response = await axios.get<ApiResponse>('/customer_issues/servman_project_communications/sql_logs', { params });
      setSqlLogs(response.data.servman_sql_logs || []);
    } catch (err: any) {
      console.error('Error fetching SQL logs:', err);
      setError(err.message || 'Failed to load SQL logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const response = await axios.get<ApiResponse>('/customer_issues/servman_project_communications/sql_logs');
      const logs = response.data.servman_sql_logs || [];
      const uniqueStatuses = Array.from(new Set(logs.map((log: ServmanSqlLog) => log.status).filter(Boolean)));
      setStatusOptions(uniqueStatuses as string[]);
    } catch (err: any) {
      console.error('Error fetching status options:', err);
    }
  };

  useEffect(() => {
    fetchSqlLogs();
    fetchStatusOptions();
  }, []);

  const handleStatusFilterChange = (event: any) => {
    const newStatus = event.target.value;
    setStatusFilter(newStatus);
    fetchSqlLogs(newStatus);
  };

  const handleViewQuery = (query: string) => {
    setSelectedQuery(query);
    setOpenDialog(true);
  };

  const handleCopyQuery = (query: string) => {
    navigator.clipboard.writeText(query);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedQuery(null);
  };

  const getLocationColor = (location: string) => {
    return location === 'pittsburgh' ? 'primary' : 'secondary';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'executed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const sqlLogsColumns = [
    {
      name: 'ID',
      selector: (row: any) => row.id,
      sortable: true,
      width: '80px',
    },
    {
      name: 'Location',
      selector: (row: any) => row.db_location,
      sortable: true,
      width: '120px',
      cell: (row: any) => (
        <Chip
          label={row.db_location === 'pittsburgh' ? 'Pittsburgh' : 'Tampa'}
          color={getLocationColor(row.db_location)}
          size="small"
        />
      ),
    },
    {
      name: 'Status',
      selector: (row: any) => row.status,
      sortable: true,
      width: '120px',
      cell: (row: any) => (
        <Chip
          label={row.status || 'N/A'}
          color={getStatusColor(row.status)}
          size="small"
        />
      ),
    },
    {
      name: 'Communication ID',
      selector: (row: any) => row.loggable_id || 'N/A',
      sortable: true,
      width: '140px',
    },
    {
      name: 'SQL Query Preview',
      selector: (row: any) => row.query,
      sortable: false,
      grow: 2,
      cell: (row: any) => <Box className={classes.queryPreview}>{row.query}</Box>,
    },
    {
      name: 'Created At',
      selector: (row: any) => new Date(row.created_at).toLocaleString(),
      sortable: true,
      width: '180px',
    },
    {
      name: 'Actions',
      width: '120px',
      cell: (row: any) => (
        <Box display="flex" gap={1}>
          <IconButton size="small" color="primary" onClick={() => handleViewQuery(row.query)} title="View Full Query">
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="secondary" onClick={() => handleCopyQuery(row.query)} title="Copy Query">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <StorageIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
              ServMan SQL Logs
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Review SQL queries generated for project communication updates
            </Typography>
          </Box>
        </Box>
        <Divider />
      </Box>

      {/* Filter */}
      <Box sx={{ mb: 3 }}>
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={handleStatusFilterChange}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    {statusOptions.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Content */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            This page displays all SQL UPDATE statements generated when project communications are updated. These
            queries are queued for synchronization with the ServMan database.
          </Typography>

          {error && (
            <Box mb={2}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          {copySuccess && (
            <Box mb={2}>
              <Chip label="Query copied to clipboard!" color="success" size="small" />
            </Box>
          )}

          <DataTable
            columns={sqlLogsColumns}
            data={sqlLogs}
            progressPending={loading}
            pagination
            paginationPerPage={50}
            paginationRowsPerPageOptions={[25, 50, 100]}
            dense
          />
        </CardContent>
      </Card>

      {/* Full Query Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Full SQL Query
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box className={classes.fullQuery}>{selectedQuery}</Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => selectedQuery && handleCopyQuery(selectedQuery)} variant="outlined">
            Copy to Clipboard
          </Button>
          <Button onClick={handleCloseDialog} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServmanSqlLogsPage;
